import * as React from "react";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import {
    Sport,
    trainerCompatibleWithSport,
    useSelectedSport,
} from "./common/context/sport";
import { useStatus } from "./hooks/status";

export interface SportSelectProps {
    showOnlyCompatible?: boolean;
    afterSportChanged?: (sport: Sport) => void;
    limitedList?: Record<string, string>;
    label?: string;
    fullWidth?: boolean;
    /** "small" to sit in a row of dense controls; the form default otherwise. */
    size?: "small" | "medium";
    /** The sport is decided elsewhere (a variation's) — show it, don't offer it. */
    disabled?: boolean;
}

export default function SportSelect({
    showOnlyCompatible = false,
    afterSportChanged,
    limitedList,
    label = "Sport",
    fullWidth = true,
    size = "medium",
    disabled = false,
}: SportSelectProps): React.JSX.Element {
    const { selected, hasSelected, available, updateSelected } =
        useSelectedSport();
    const { status } = useStatus();

    const handleChange = (event: SelectChangeEvent) => {
        const sport = event.target.value as Sport;
        updateSelected(sport);
        if (afterSportChanged) {
            afterSportChanged(sport);
        }
    };

    const filteredSports = Object.keys(limitedList ?? available).filter((a) => {
        if (showOnlyCompatible) {
            return trainerCompatibleWithSport(a as Sport, status);
        }
        return true;
    });

    return (
        <FormControl fullWidth={fullWidth} size={size} disabled={disabled}>
            <InputLabel id="sport-select-label">{label}</InputLabel>
            <Select
                labelId="sport-select-label"
                id="sport-select"
                value={hasSelected ? selected : ""}
                label={label}
                onChange={handleChange}
            >
                {filteredSports.map((k) => {
                    let itemLabel = limitedList
                        ? limitedList[k as Sport]
                        : available[k as Sport];
                    if (itemLabel === "Pickleball" && status) {
                        itemLabel += " (Requires Pickleball Trainer)";
                    }
                    return (
                        <MenuItem key={k} value={k}>
                            {itemLabel}
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
}
